import { Component, OnDestroy, OnInit, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DashboardWidgets } from 'dashboard/enums/dashboard-widget.enum';
import { DashboardListingsStatusSummary } from 'dashboard/interfaces/dashboard-listings-status-summary.interface';
import { DashboardListingsNotUpdated } from 'dashboard/interfaces/dashboard-not-updated-listings.interface';
import { DashboardWidgetConfigurationInterface } from 'dashboard/interfaces/dashboard-widget-configuration.interface';
import { ListTypeInterface } from 'dashboard/interfaces/list-type.interface';
import { NameValueInterface } from 'dashboard/interfaces/name-value.interface';
import { DashboardService } from 'dashboard/services/dashboard.service';
import { Subscription } from 'rxjs';
import { IMAGES } from 'shared/constants/imagePaths.constants';
import { CurrentShijiUserInterface } from 'shared/interfaces/current-shiji-user.interface';
import { CurrentSupplierUserInterface } from 'shared/interfaces/current-supplier-user.interface';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { AccessTypeEnum, PermissionAccessZoneEnum, ShijiPermissionsEnum } from 'shared/interfaces/permissions.interface';
import { UserApiService } from 'shared/services';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { supplierUserHasAccess } from 'shared/util/supplierUserHasAccess';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.scss'],
    standalone: false,
})
export class DashboardComponent implements OnInit, OnDestroy {
    protected readonly featureFlagEnum = FeatureFlagEnum;

    listingsStatusSummaryWidgetConfiguration?: DashboardWidgetConfigurationInterface;
    listingsNotUpdatedWidgetConfiguration?: DashboardWidgetConfigurationInterface;
    listingsStatusSummaryData?: DashboardListingsStatusSummary;
    listingsNotUpdated: DashboardListingsNotUpdated[] = [];
    private subscriptions: { [key: string]: Subscription } = {};
    protected IMAGES = IMAGES;

    hasAccessToChannelSnapshotConfigs = signal<boolean>(false);
    isShijiUser = signal<boolean>(false);

    constructor(
        private dashboardService: DashboardService,
        private translateService: TranslateService,
        private userApiService: UserApiService,
        public featureFlagService: FeatureFlagService,
    ) {}

    ngOnInit(): void {
        this.subscriptions.onLangChange = this.translateService.onLangChange.subscribe(() => {
            this._generateListingsStatusSummaryWidgetConfiguration();
            this._generateListingsNotUpdatedWidgetConfiguration();
        });

        this.subscriptions.getPublicationStatus = this.dashboardService.getPublicationStatusWidgetData().subscribe((res) => {
            this.listingsStatusSummaryData = res;
            this._generateListingsStatusSummaryWidgetConfiguration();
        });

        this.subscriptions.getNotUpdated = this.dashboardService.getNotUpdatedListings().subscribe((res) => {
            this.listingsNotUpdated = res;
            this._generateListingsNotUpdatedWidgetConfiguration();
        });

        this.subscriptions.getLoginInformation = this.userApiService.getLoginInformationContinuously().subscribe((loginInfo) => {
            this.isShijiUser.set(loginInfo.isShiji);

            if (!loginInfo.isShiji) {
                const supplierUserDetails = loginInfo as CurrentSupplierUserInterface;
                const hasAccessToSnapshots = supplierUserHasAccess(
                    supplierUserDetails.internalPermissions,
                    PermissionAccessZoneEnum.ACCESS_TO_CHANNEL_SNAPSHOT_REGENERATION,
                    AccessTypeEnum.READ,
                );
                const hasAccessToBulkOperations = supplierUserHasAccess(
                    supplierUserDetails.internalPermissions,
                    PermissionAccessZoneEnum.ACCESS_TO_BULK_OPERATIONS,
                    AccessTypeEnum.READ,
                );
                this.hasAccessToChannelSnapshotConfigs.set(hasAccessToSnapshots && hasAccessToBulkOperations);
            } else {
                const shijiUserDetails = loginInfo as CurrentShijiUserInterface;
                const hasAccessToBulkOperations = shijiUserDetails?.shijiPermissions?.find(
                    (permission) => permission.shijiAccessZone === ShijiPermissionsEnum.BULK_OPERATIONS,
                )?.isAuthorized;
                this.hasAccessToChannelSnapshotConfigs.set(hasAccessToBulkOperations ?? false);
            }
        });
    }

    public ngOnDestroy(): void {
        unsubscribeFromAll(this.subscriptions);
    }

    private _generateListingsNotUpdatedWidgetConfiguration() {
        const data: ListTypeInterface[] = this.listingsNotUpdated.map((item) => {
            return {
                name: item.listingName,
                value: item.listingId,
                date: item.lastModified,
            };
        });

        this.listingsNotUpdatedWidgetConfiguration = {
            data: data,
            title: this.translateService.instant('dashboard.listingsNotUpdatedInLast30Days'),
            labels: [
                this.translateService.instant('dashboard.listingNameRowHeader'),
                this.translateService.instant('dashboard.listingLastUpdatedRowHeader'),
            ],
            navigationTitle: this.translateService.instant('dashboard.navigateToListingTitle'),
            dashboardWidget: DashboardWidgets.LISTINGS_WITHOUT_UPDATES,
            noDataText: this.translateService.instant('dashboard.noListingsNotUpdatedInLast30Days'),
        };
    }

    private _generateListingsStatusSummaryWidgetConfiguration() {
        const data: NameValueInterface[] = [
            'live',
            'draft',
            'publishingFailures',
            'returnedToEditor',
            'waitingForApproval',
            'neverLive',
        ].map((item) => {
            const value = (this.listingsStatusSummaryData as { [key: string]: any })?.[item + 'Listings'] ?? 0;
            const name = this.translateService.instant('dashboard.' + item, {
                nb: (this.listingsStatusSummaryData as { [key: string]: any })?.[item + 'Listings'],
            });

            return {
                value: value,
                name: name,
                extraData: item,
            };
        });

        const labels: string[] = ['live', 'draft', 'publishingFailures', 'returnedToEditor', 'waitingForApproval', 'neverLive'].map(
            (item) =>
                this.translateService.instant('dashboard.' + item, {
                    nb: (this.listingsStatusSummaryData as { [key: string]: any })?.[item + 'Listings'],
                }),
        );

        this.listingsStatusSummaryWidgetConfiguration = {
            data: data,
            labels: labels,
            total: this.listingsStatusSummaryData?.totalListings ?? 0,
            title: this.translateService.instant('dashboard.listingsStatusSummaryWidgetTitle'),
        };
    }
}
